<template>
  <div class="card border-2 mb-4 shadow-lg d-flex flex-column position-relative"
  :class="{
    'card-hover': !selectable,
    pointer: isClickToSelect,
    'network-card-selectable': isClickToSelect && !selected,
    'border-primary network-card-selected': selectable && selected,
  }"
  :style="{ width: '100%', minHeight: hideFooter ? '215px' : '250px' }"
  :role="isClickToSelect ? 'button' : null"
  :tabindex="isClickToSelect ? 0 : null"
  :aria-pressed="isClickToSelect ? String(selected) : null"
  @click="onCardClick"
  @keydown.enter.prevent="onCardClick"
  @keydown.space.prevent="onCardClick">
    <div v-if="selectable && selected" class="network-card-selected-badge">
      <i class="fas fa-check-circle"></i>
    </div>
    <div class="card-body flex-grow-1">
      <div class="d-flex align-items-start">
        <avatar
          :username="network.name"
          :src="network.logo_url"
          :size="50"
          color="white"
          class="img me-3 bg-gradient-primary-light"
          style="border-radius: 20% !important;"
        ></avatar>
        <div class="d-flex flex-column text-secondary fw-semibold">
          <div class="d-flex align-items-center mb-2">
            <span v-if="networkTags.length === 0">
              <span v-if="network.public" class="small me-2">
                <i class="fas fa-lock-open me-1"></i>
                  {{ $t('NetworkCard.public-access') }}
              </span>
              <span v-else class="small me-2">
                <i class="fas fa-lock me-1"></i>
                  {{ $t('NetworkCard.private-access') }}
              </span>
            </span>
            <span 
              v-for="tag in networkTags.slice(0, 2)" 
              :key="tag"
              class="badge text-white fw-normal p-1 me-2"
              :style="{ backgroundColor: getTagColor(tag) }"
            >
              {{ getTagLabel(tag) }}
            </span>
          </div>
          <div class="d-flex align-items-center">
            <span v-if="networkTags.length === 0" class="small me-2">
              <i class="fas fa-user-group"></i>
              {{ network.num_users }}
              {{ $t('members') }}
            </span>
            <span 
              v-for="tag in networkTags.slice(2, 4)" 
              :key="tag"
              class="badge text-white fw-normal p-1 me-2"
              :style="{ backgroundColor: getTagColor(tag) }"
            >
              {{ getTagLabel(tag) }}
            </span>
          </div>
        </div>
      </div>
      <div class="card-title m-0 pt-2">
        <span v-if="selectable" class="fs-5" :title="network.name">{{ truncatedNetworkName }}</span>
        <router-link v-else :to="{ name: 'network-home', params: { slug: network.slug } }" class="fs-5" style="text-decoration: none;" :title="network.name">
          {{ truncatedNetworkName }}
        </router-link>
      </div>
      <div v-if="network.purpose" class="card-text" :class="{ pointer: !selectable }">
        <span v-if="selectable" style="font-size: 15px;" :title="network.purpose">
          {{ truncatedNetworkPurpose.text }}
        </span>
        <router-link v-else :to="{ name: 'network-home', params: { slug: network.slug } }" style="all: unset; font-size: 15px;">
          <span :title="network.purpose">
            {{ truncatedNetworkPurpose.text }}
          </span>
          <span v-if="truncatedNetworkPurpose.hasMore" class="text-primary">
            {{ $t('CardSmall.read-more') }}
          </span>
        </router-link>
      </div>
    </div>
    <div v-if="!hideFooter" class="card-footer border-0 pt-2">
      <small
        v-if="isMember"
        class="float-start text-info fw-semibold">
        <strong>
          <i class="fas fa-check"></i>
          {{ $t('already-a-member') }}
        </strong>
      </small>
      <small
        v-else-if="isJoinPending"
        class="float-start text-primary fw-semibold">
        <strong>
          <i class="fas fa-exclamation-circle"></i>
          {{ $t('your-request-to-join-this-network-is-being-reviewed-we-will-get-back-to-you-soon') }}
        </strong>
      </small>
      <small
        v-else-if="isJoinRejected"
        class="float-start text-muted fw-semibold">
        <strong>
          <i class="fa fa-ban"></i>
          {{ $t('NetworkCard.your-request-has-been-rejected') }}
        </strong>
      </small>
      <small
        v-else-if="justRequested"
        class="float-start text-success fw-semibold">
        <strong>
          <i class="fas fa-check"></i>
          {{ $t('requested') }}
        </strong>
      </small>
      <button
        v-else-if="!selectable && showJoin && canJoin"
        type="button"
        data-test="join"
        :disabled="busy"
        class="btn btn-link float-start text-primary fw-semibold pointer p-0 text-decoration-none"
        @click="join"
      >
        <span v-if="network.moderation">{{ $t('apply-for-membership') }}</span>
        <span v-else>{{ $t('become-a-member') }}</span>
      </button>
      <!-- Empty space to maintain consistent footer height when no content -->
      <div v-if="!hasFooterContent" style="min-height: 1px;"></div>
    </div>
  </div>
</template>
<script>
import { JoinNetworkMixin } from '@/mixins/Network/JoinNetworkMixin.js';
import Avatar from 'vue-avatar';
import { mapState } from 'vuex';
import { getTagColor } from '@/utils/tagUtils.js';

export default {
  components: {
    Avatar,
  },
  mixins: [JoinNetworkMixin],
  props: {
    network: {
      type: Object,
      default: null,
    },
    showJoin: {
      type: Boolean,
      default: true,
    },
    hideFooter: {
      type: Boolean,
      default: false,
    },
    // when true, the card becomes a click-to-toggle selection target instead
    // of showing a footer join button - used by the Links landing page's
    // multi-select flow
    selectable: {
      type: Boolean,
      default: false,
    },
    selected: {
      type: Boolean,
      default: false,
    },
    // purely client-side, ephemeral confirmation that a join/request was just
    // submitted for this network in the current page session (e.g. right
    // after a logged-out registration) - not persisted, gone on refresh
    justRequested: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      busy: false,
    };
  },
  computed: {
    ...mapState({
      token: state => state.auth.token,
    }),
    canJoin() {
      return !this.network.is_mine && this.network.public;
    },
    isMember() {
      return !!(this.network && this.network.is_mine);
    },
    isClickToSelect() {
      return this.selectable && this.canJoin && !this.isMember && !this.isJoinPending && !this.isJoinRejected && !this.justRequested;
    },
    isJoinPending() {
      const n = this.network;
      if (!n) return false;
      return (n.is_in_moderation_queue && n.join_status === 'PENDING') ||
             (n.is_in_industry_moderation_queue && n.industry_join_status === 'PENDING');
    },
    isJoinRejected() {
      // only show rejected if not pending (pending takes precedence)
      if (this.isJoinPending) return false;
      const n = this.network;
      if (!n) return false;
      return (n.is_in_moderation_queue && n.join_status === 'REJECTED') ||
             (n.is_in_industry_moderation_queue && n.industry_join_status === 'REJECTED');
    },
    hasFooterContent() {
      return this.isMember ||
             this.isJoinPending ||
             this.isJoinRejected ||
             this.justRequested ||
             (!this.selectable && this.showJoin && this.canJoin);
    },
    truncatedNetworkName() {
      if (!this.network || !this.network.name) return '';
      return this.network.name.length > 60 
        ? this.network.name.slice(0, 60) + '...' 
        : this.network.name;
    },
    truncatedNetworkPurpose() {
      if (!this.network || !this.network.purpose) {
        return { text: '', hasMore: false };
      }
      if (this.network.purpose.length > 90) {
        return {
          text: this.network.purpose.slice(0, 90) + '...',
          hasMore: true
        };
      }
      return {
        text: this.network.purpose,
        hasMore: false
      };
    },
    networkTags() {
      if (!this.network || !this.network.tags) return [];

      // ensure we're working with an array
      if (Array.isArray(this.network.tags)) {
        return this.network.tags;
      }

      // if it's a string (JSON), parse it
      if (typeof this.network.tags === 'string') {
        try {
          const parsed = JSON.parse(this.network.tags);
          return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
          console.warn('Failed to parse network tags:', this.network.tags);
          return [];
        }
      }

      return [];
    },
  },
  methods: {
    getTagLabel(tagId) {
      // map tag IDs to human-readable labels with reactive translations
      switch (tagId) {
        // frequency tags
        case 'meets-weekly':
          return this.$t('meets-weekly');
        case 'meets-monthly':
          return this.$t('meets-monthly');
        case 'meets-regularly':
          return this.$t('meets-regularly');
        // tumor board tags
        case 'oncology':
          return this.$t('oncology');
        case 'leukemia':
          return this.$t('leukemia');
        case 'neuroblastoma':
          return this.$t('neuroblastoma');
        case 'solid-tumors':
          return this.$t('solid-tumors');
        case 'palliative':
          return this.$t('palliative');
        // other tags
        case 'webinar':
          return this.$t('webinar');
        case 'case-discussion':
          return this.$t('case-discussion');
        case 'research':
          return this.$t('research');
        case 'peer-training':
          return this.$t('peer-training');
        case 'resource-library':
          return this.$t('resource-library');
        default:
          return tagId;
      }
    },
    getTagColor,
    onCardClick() {
      if (this.isClickToSelect) {
        this.$emit('toggle', this.network.slug);
      }
    },
    async join() {
      this.busy = true;
      // check if user has required data
      if (this.$auth.auth && this.$auth.user) {
        const user = this.$auth.user;
        const hasProfessionType = user.is_hcp || user.is_industry || user.is_other;
        const hasWorkplace = user.workplace && user.workplace.trim() !== '';
        // if user has no profession or workplace, show alert
        if (!hasProfessionType || !hasWorkplace) {
          this.busy = false;
          await this.$swal.fire({
            title:  this.$t('incomplete-data'),
            text: this.$t('please-finish-filling-out-your-details-before-joining-this-network'),
            icon: 'warning',
            confirmButtonText: this.$t('network.user.visitAccount'),
            customClass: {
              confirmButton: 'btn btn-primary mx-1'
            },
            buttonsStyling: false,
            allowOutsideClick: false,
            allowEscapeKey: false,
          }).then(({ isConfirmed }) => {
            if (isConfirmed) {
              // redirect to account page
              this.$router.push({ name: 'account' });
            }
          });
          return;
        }
      }
      try {
        await this.joinNetwork(this.network);
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>
<style scoped>
/* this card is a div[role="button"][tabindex] for keyboard support - nothing
   in this app resets the browser's native focus outline for that, so clicking
   a card (which focuses it) can show a competing default ring on top of our
   own selected/hover styling. Reset it, but keep a visible indicator for
   keyboard-only focus (:focus-visible skips mouse-triggered focus). */
.card:focus {
  outline: none;
}
.card:focus-visible {
  outline: 2px solid var(--bs-primary, #005f9a);
  outline-offset: 2px;
}
.network-card-selectable {
  transition: box-shadow 0.15s;
}
.network-card-selectable:hover {
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.1) !important;
}
.network-card-selected {
  border-color: var(--bs-primary, #005f9a) !important;
  box-shadow: 0 0 0 1px var(--bs-primary, #005f9a) !important;
}
.network-card-selected-badge {
  position: absolute;
  top: -8px;
  right: -8px;
  z-index: 1;
  color: var(--bs-primary, #005f9a);
  background: #fff;
  border-radius: 50%;
  font-size: 1.4rem;
  line-height: 1;
}
.card-body {
  padding: 15px 20px 0 20px !important;
}
.card-footer {
  padding-right: 20px !important;
  padding-left: 20px !important;
  min-height: 40px;
}
.network-title {
  font-size: 20px;
}
</style>
